/**
 * @description 促销标签解析共享工具——购物车/订单确认/订单详情复用
 */

import { PROMOTION_TYPE } from '~/utils/store/promotionTypes'
import { shouldShowOrderPromotionUi } from '~/utils/store/promotionActivityInProgress'
import { resolveGiveawayPurchasedQty } from '~/utils/store/giveawayGiftDisplay'

/**
 * @param {number|string} value
 * @param {number} [maxDecimals=2]
 * @returns {string}
 */
export function formatPromoNum(value, maxDecimals = 2) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(String(value).replace(/[^\d.]/g, ''))
  if (!Number.isFinite(n)) return ''
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(maxDecimals)))
}

/**
 * @param {string} text
 * @returns {string}
 */
export function normalizePromoTagUnit(text) {
  let normalized = String(text || '').trim()
  if (!normalized) return ''

  normalized = normalized
    .replace(/^每满(\d+(?:\.\d+)?)件享赠品$/, '满$1台享赠品')
    .replace(/每满(\d+(?:\.\d+)?)台享赠品/g, '满$1台享赠品')
    .replace(/满(\d+(?:\.\d+)?)件/g, '满$1台')
    .replace(/\/件/g, '/台')
    .replace(/满(\d+(?:\.\d+)?)台享(\d+(?:\.\d+)?)元\/台/g, '满$1台享¥$2/台')
    .replace(/已减(\d+(?:\.\d+)?)元\/台/g, '已减¥$1/台')
    .replace(/已减(\d+(?:\.\d+)?)\/台/g, '已减¥$1/台')
    .replace(/减(\d+(?:\.\d+)?)元/g, '减¥$1')
    .replace(/¥\s+/g, '¥')

  return normalized
}

/**
 * 从阶梯列表中选取应展示的阶梯
 * @param {Array} hierarchies - 阶梯列表（按 buyNum 升序）
 * @param {number} currentQty - 当前数量
 * @returns {Object|null}
 */
export function selectTierToDisplay(hierarchies, currentQty) {
  if (!Array.isArray(hierarchies) || hierarchies.length === 0) return null

  const sorted = [...hierarchies].sort((a, b) => (Number(a.buyNum) || 0) - (Number(b.buyNum) || 0))

  if (sorted.length === 1) return sorted[0]

  for (const tier of sorted) {
    if (currentQty < (Number(tier.buyNum) || 0)) {
      return tier
    }
  }

  return sorted[sorted.length - 1]
}

/**
 * 从阶梯列表中选取已满足的最高阶梯
 * @param {Array} hierarchies
 * @param {number} currentQty
 * @returns {Object|null}
 */
export function selectAppliedTier(hierarchies, currentQty) {
  if (!Array.isArray(hierarchies) || hierarchies.length === 0) return null

  const sorted = [...hierarchies].sort((a, b) => (Number(a.buyNum) || 0) - (Number(b.buyNum) || 0))

  let applied = null
  for (const tier of sorted) {
    if (currentQty >= (Number(tier.buyNum) || 0)) {
      applied = tier
    }
  }
  return applied
}

/**
 * 单价列促销标签
 * @param {Object} record
 * @param {(record: Object) => boolean} [shouldShow]
 * @returns {string}
 */
export function resolveUnitPromotionTag(record, shouldShow) {
  const check = shouldShow || shouldShowOrderPromotionUi
  if (!record?.isProduct && !record?.promotionType) return ''
  if (!check(record)) return ''

  const explicit = String(
    record.unitPromotionTag ??
      record.appliedPromotionTag ??
      record.enjoyedPromotionTag ??
      record.appliedDiscountTag ??
      ''
  ).trim()
  if (explicit) return normalizePromoTagUnit(explicit)

  const pt = Number(record.promotionType)
  const hierarchies = Array.isArray(record.promotionHierarchies) ? record.promotionHierarchies : []
  const qty = Number(record.quantity ?? record.count ?? record.prodCount ?? 0) || 0

  if (pt === PROMOTION_TYPE.VOLUME_PRICE && hierarchies.length > 0) {
    const appliedTier = selectAppliedTier(hierarchies, qty)
    if (appliedTier && appliedTier.promotionPrice) {
      const originalPrice = Number(record.originalUnitPrice) || Number(record.price) || 0
      const promoPrice = Number(appliedTier.promotionPrice)
      const saved = originalPrice - promoPrice
      if (saved > 0.005) {
        return `已减¥${formatPromoNum(saved)}/台`
      }
    }
  }

  if (pt === PROMOTION_TYPE.VOLUME_DISCOUNT && hierarchies.length > 0) {
    const appliedTier = selectAppliedTier(hierarchies, qty)
    if (appliedTier && appliedTier.promotionDiscount) {
      const discountRate = Number(appliedTier.promotionDiscount)
      const zhe = discountRate * 10
      if (zhe > 0 && zhe < 10) {
        return `已享${formatPromoNum(zhe, 1)}折`
      }
    }
  }

  return ''
}

/**
 * 买赠活动标签：仅展示当前已满足的最高阶梯（不展示下一档）
 * @param {Object} record
 * @param {{ qtyByPromotionProdKey?: Map<string, number> }} [options] - 传入时按「同活动同商品」合并件数判断阶梯
 * @returns {string}
 */
export function resolveBuyGiftPromotionTag(record, options = {}) {
  const pt = Number(record?.promotionType)
  if (pt !== PROMOTION_TYPE.BUY_GIFT) return ''

  const qtyMap = options.qtyByPromotionProdKey
  const qty =
    qtyMap instanceof Map
      ? resolveGiveawayPurchasedQty(record, qtyMap)
      : Number(
          record?.quantity ?? record?.count ?? record?.prodCount ?? record?.basketCount ?? 0
        ) || 0
  const hierarchies = Array.isArray(record?.promotionHierarchies) ? record.promotionHierarchies : []

  if (hierarchies.length > 0) {
    const tier = selectAppliedTier(hierarchies, qty)
    const target =
      tier || [...hierarchies].sort((a, b) => (Number(a.buyNum) || 0) - (Number(b.buyNum) || 0))[0]
    const buyNum = Number(target.buyNum) || 0
    return buyNum > 0 ? normalizePromoTagUnit(`满${buyNum}台享赠品`) : ''
  }

  const buyNum = Number(record?.giveawayVO?.buyNum ?? record?.buyNum)
  if (Number.isFinite(buyNum) && buyNum > 0) {
    return normalizePromoTagUnit(`满${buyNum}台享赠品`)
  }

  return ''
}

/**
 * 数量列促销标签
 * @param {Object} record
 * @param {(record: Object) => boolean} [shouldShow]
 * @param {{ qtyByPromotionProdKey?: Map<string, number> }} [options]
 * @returns {string}
 */
export function resolveQuantityPromotionTag(record, shouldShow, options = {}) {
  const check = shouldShow || shouldShowOrderPromotionUi
  if (!record?.isProduct && !record?.promotionType) return ''
  if (!check(record)) return ''

  const pt = Number(record.promotionType)
  if (pt === PROMOTION_TYPE.BUY_GIFT) {
    return resolveBuyGiftPromotionTag(record, options)
  }

  const explicit = String(record.quantityPromotionTag ?? record.nextPromotionTag ?? '').trim()
  if (explicit) return normalizePromoTagUnit(explicit)

  const hierarchies = Array.isArray(record.promotionHierarchies) ? record.promotionHierarchies : []
  const qty = Number(record.quantity ?? record.count ?? record.prodCount ?? 0) || 0

  if (pt === PROMOTION_TYPE.VOLUME_PRICE && hierarchies.length > 0) {
    const tier = selectTierToDisplay(hierarchies, qty)
    if (tier && tier.promotionPrice) {
      const buyNum = Number(tier.buyNum) || 0
      const price = formatPromoNum(tier.promotionPrice)
      if (buyNum > 0 && price) {
        return `满${buyNum}台享¥${price}/台`
      }
    }
  }

  if (pt === PROMOTION_TYPE.VOLUME_DISCOUNT && hierarchies.length > 0) {
    const tier = selectTierToDisplay(hierarchies, qty)
    if (tier && tier.promotionDiscount) {
      const buyNum = Number(tier.buyNum) || 0
      const discountRate = Number(tier.promotionDiscount)
      const zhe = discountRate * 10
      if (buyNum > 0 && zhe > 0 && zhe < 10) {
        return `满${buyNum}台享${formatPromoNum(zhe, 1)}折`
      }
    }
  }

  return ''
}
